import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../api';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './DashboardPage.module.css';

// ── Icons (inline SVG helpers) ─────────────────────────────────────────────
const Icon = ({ d, size = 16, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" {...rest}>
    {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
  </svg>
);

const SpinnerIcon = ({ size = 18 }) => (
  <svg className={styles.spinnerIcon} width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

// ── Utility ────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Safe API fetch — reads body as text first, then attempts JSON.parse.
 * Throws a meaningful error when the server is unreachable (returns HTML).
 */
const safeApiFetch = async (url, options = {}) => {
  let res;
  try {
    res = await fetch(url, options);
  } catch (networkErr) {
    throw new Error('Cannot reach the server. Make sure the backend is running on port 5000.');
  }

  const text = await res.text();

  let data;
  try {
    data = JSON.parse(text);
  } catch {
    // The server returned HTML — likely a 404 from CRA proxy (backend not running / route missing)
    if (!res.ok) {
      throw new Error(
        `Server returned ${res.status} (non-JSON). ` +
        'Restart the backend server and make sure all routes are loaded.'
      );
    }
    throw new Error('Server returned an unexpected non-JSON response.');
  }

  if (!res.ok) {
    throw new Error(data?.message || data?.errors?.[0]?.msg || `Request failed (${res.status})`);
  }

  return data;
};

// ═══════════════════════════════════════════════════════════════════════════════
// PANEL 1 — Package Management
// ═══════════════════════════════════════════════════════════════════════════════
const PackageManagement = ({ token }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const data = await safeApiFetch(getApiUrl('/api/app-config'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const normalized = (data.packages || []).map((p) => ({
          ...p,
          descriptionPoints: (p.descriptionPoints || []).map((point) =>
            typeof point === 'string' ? { en: point, ar: '' } : { en: point.en || '', ar: point.ar || '' }
          ),
        }));
        setPackages(normalized);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const updatePackageField = (index, field, value) =>
    setPackages((cur) => cur.map((p, i) => (i === index ? { ...p, [field]: value } : p)));

  const updatePaymentField = (index, field, value) =>
    setPackages((cur) =>
      cur.map((p, i) =>
        i === index
          ? { ...p, paymentMethod: { ...p.paymentMethod, [field]: Number(value) } }
          : p
      )
    );

  const updatePoint = (pi, qi, lang, val) =>
    setPackages((cur) =>
      cur.map((p, i) => {
        if (i !== pi) return p;
        const pts = [...(p.descriptionPoints || [])];
        pts[qi] = { ...pts[qi], [lang]: val };
        return { ...p, descriptionPoints: pts };
      })
    );

  const addPoint = (pi) =>
    setPackages((cur) =>
      cur.map((p, i) =>
        i === pi ? { ...p, descriptionPoints: [...(p.descriptionPoints || []), { en: '', ar: '' }] } : p
      )
    );

  const removePoint = (pi, qi) =>
    setPackages((cur) =>
      cur.map((p, i) => {
        if (i !== pi) return p;
        const pts = [...(p.descriptionPoints || [])];
        pts.splice(qi, 1);
        return { ...p, descriptionPoints: pts };
      })
    );

  const savePackages = async () => {
    setSaving(true);
    setStatus(null);
    setError(null);
    try {
      await safeApiFetch(getApiUrl('/api/app-config'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ packages }),
      });
      setStatus('Package configuration saved successfully.');
    } catch (err) {
      setError(err.message || 'Unable to save package details.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <SpinnerIcon size={28} />
        <span>Loading packages…</span>
      </div>
    );
  }

  return (
    <>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>Package Configuration</div>
        <div className={styles.sectionDesc}>
          Edit the pricing plans shown on the public pricing page. Changes are saved to the database immediately.
        </div>
      </div>

      {error && (
        <div className={styles.alertDanger} role="alert">
          <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          {error}
        </div>
      )}
      {status && (
        <div className={styles.alertSuccess} role="alert">
          <Icon d={['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4 12 14.01l-3-3']} />
          {status}
        </div>
      )}

      {packages.map((pkg, pi) => (
        <div className={styles.packageCard} key={pkg._id || pi}>
          <div className={styles.packageCardHeader}>
            <span className={styles.packageName}>
              {pkg.name || `Plan ${pi + 1}`}
            </span>
            <span className={styles.packageBadge}>Plan {pi + 1}</span>
          </div>
          <div className={styles.packageCardBody}>
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Name</label>
                <input
                  className={styles.fieldInput}
                  type="text"
                  value={pkg.name || ''}
                  onChange={(e) => updatePackageField(pi, 'name', e.target.value)}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Monthly price ($)</label>
                <input
                  className={styles.fieldInput}
                  type="number"
                  value={pkg.paymentMethod?.monthly ?? 0}
                  onChange={(e) => updatePaymentField(pi, 'monthly', e.target.value)}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Annual price ($)</label>
                <input
                  className={styles.fieldInput}
                  type="number"
                  value={pkg.paymentMethod?.annually ?? 0}
                  onChange={(e) => updatePaymentField(pi, 'annually', e.target.value)}
                />
              </div>
            </div>

            <div className={styles.pointsSection}>
              <div className={styles.pointsLabel}>Description Points</div>
              {(pkg.descriptionPoints || []).map((point, qi) => (
                <div className={styles.pointRow} key={qi}>
                  <span className={styles.pointBullet} />
                  <input
                    className={styles.pointInput}
                    type="text"
                    value={point.en || ''}
                    onChange={(e) => updatePoint(pi, qi, 'en', e.target.value)}
                    placeholder={`Feature ${qi + 1} (English)`}
                  />
                  <input
                    className={styles.pointInput}
                    type="text"
                    dir="rtl"
                    lang="ar"
                    value={point.ar || ''}
                    onChange={(e) => updatePoint(pi, qi, 'ar', e.target.value)}
                    placeholder={`Feature ${qi + 1} (Arabic)`}
                  />
                  <button
                    type="button"
                    className={styles.removePointBtn}
                    onClick={() => removePoint(pi, qi)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={styles.addPointBtn}
                onClick={() => addPoint(pi)}
              >
                <Icon d="M12 5v14M5 12h14" size={14} />
                Add feature
              </button>
            </div>
          </div>
        </div>
      ))}

      <div className={styles.saveBar}>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={savePackages}
          disabled={saving}
        >
          {saving ? <SpinnerIcon size={16} /> : <Icon d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" size={16} />}
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PANEL 3 — Audit Request Detail
// ═══════════════════════════════════════════════════════════════════════════════
const AuditDetail = ({ token, requestId, onBack }) => {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPlaceModal, setShowPlaceModal] = useState(false);
  const [placeEntry, setPlaceEntry] = useState('');
  const [placeMode, setPlaceMode] = useState('');
  const [savingPlaceInfo, setSavingPlaceInfo] = useState(false);
  const [placeError, setPlaceError] = useState(null);
  const [reviewsTab, setReviewsTab] = useState('details');

  useEffect(() => {
    if (!token || !requestId) return;
    const load = async () => {
      try {
        const data = await safeApiFetch(getApiUrl(`/api/audit/admin/${requestId}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRequest(data.auditRequest);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, requestId]);

  const handleSavePlaceInfo = async () => {
    const trimmed = placeEntry?.trim();
    if (!trimmed) {
      setPlaceError('Please enter a Google Maps URL or a Place ID.');
      return;
    }

    setSavingPlaceInfo(true);
    setPlaceError(null);

    const payload = { auditRequestId: requestId };
    if (placeMode === 'placeId') {
      payload.placeId = trimmed;
    } else {
      // default to URL mode
      payload.googleMapsUrl = trimmed;
    }

    try {

      if(Object.keys(payload).length == 0) {
        setShowPlaceModal(false);
        setPlaceEntry('');
        return;
      }

      const data = await safeApiFetch(getApiUrl('/api/audit/update-audit'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if(data.auditRequest){
        setRequest(data.auditRequest);
      }
      setShowPlaceModal(false);
      setPlaceEntry('');
    } catch (err) {
      setPlaceError(err.message);
    } finally {
      setSavingPlaceInfo(false);
    }
  };

  const parseReviewRating = (review) => {
    const ratingValue = review?.rating ?? review?.stars ?? review?.ratingValue ?? review?.ratingLabel;
    if (typeof ratingValue === 'number' && !Number.isNaN(ratingValue)) {
      return ratingValue;
    }
    if (typeof ratingValue === 'string') {
      const numeric = Number(ratingValue.trim());
      if (!Number.isNaN(numeric)) return numeric;
      const match = ratingValue.match(/([1-5](?:\.\d)?)/);
      if (match) return Number(match[1]);
    }

    const reviewText = (review?.review || review?.text || review?.author || '').toString();
    const textMatch = reviewText.match(/([1-5])(?:\/5|\s*★|\s*stars?)/i);
    if (textMatch) return Number(textMatch[1]);

    return null;
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <SpinnerIcon size={28} />
        <span>Loading audit details…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.alertDanger} role="alert">
        <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        {error}
      </div>
    );
  }

  const r = request;
  const statusClass = r?.response?.status || 'pending';

  return (
    <>
      <button type="button" className={styles.detailBackBtn} onClick={onBack}>
        <Icon d="M19 12H5M12 5l-7 7 7 7" size={15} />
        Back to Audit Requests
      </button>

      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>{r.business}</div>
        <div className={styles.sectionDesc}>
          {r.city}, {r.state} &mdash; submitted {formatDate(r.createdAt)}
        </div>
      </div>

      <div className={styles.detailGrid}>
        {/* Business Info */}
        <div className={styles.detailCard}>
          <div className={styles.detailCardTitle}>Business Information</div>
          <div className={styles.detailRow}>
            <span className={styles.detailKey}>Business Name</span>
            <span className={styles.detailVal}>{r.business}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailKey}>City</span>
            <span className={styles.detailVal}>{r.city}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailKey}>State</span>
            <span className={styles.detailVal}>{r.state}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailKey}>Submitted</span>
            <span className={styles.detailVal}>{formatDate(r.createdAt)}</span>
          </div>
        </div>

        {/* Submitter Info */}
        <div className={styles.detailCard}>
          <div className={styles.detailCardTitle}>Submitted By</div>
          <div className={styles.detailRow}>
            <span className={styles.detailKey}>Name</span>
            <span className={styles.detailVal}>{r.userId?.name || <em className={styles.detailVal + ' ' + styles.muted}>Anonymous</em>}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailKey}>Email</span>
            <span className={styles.detailVal}>{r.userId?.email || <em>—</em>}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailKey}>IP Address</span>
            <span className={styles.detailVal}>{r.ipAddress || '—'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailKey}>User Agent</span>
            <span className={styles.detailVal} style={{ fontSize: '0.75rem', maxWidth: '240px' }}>
              {r.userAgent || '—'}
            </span>
          </div>
        </div>

        {/* Audit Response with Tabs */}
        <div className={`${styles.detailCard} ${styles.fullWidth}`}>
          <div className={styles.detailCardTitle}>Audit Response</div>
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '16px', gap: '4px' }}>
            <button
              type="button"
              onClick={() => setReviewsTab('details')}
              style={{
                padding: '8px 16px',
                border: 'none',
                background: reviewsTab === 'details' ? '#f3f4f6' : 'transparent',
                borderBottom: reviewsTab === 'details' ? '2px solid #3b82f6' : 'none',
                cursor: 'pointer',
                fontWeight: reviewsTab === 'details' ? '500' : '400',
                color: reviewsTab === 'details' ? '#1f2937' : '#6b7280',
              }}
            >
              Details
            </button>
            <button
              type="button"
              onClick={() => setReviewsTab('reviews')}
              style={{
                padding: '8px 16px',
                border: 'none',
                background: reviewsTab === 'reviews' ? '#f3f4f6' : 'transparent',
                borderBottom: reviewsTab === 'reviews' ? '2px solid #3b82f6' : 'none',
                cursor: 'pointer',
                fontWeight: reviewsTab === 'reviews' ? '500' : '400',
                color: reviewsTab === 'reviews' ? '#1f2937' : '#6b7280',
              }}
            >
              Reviews
            </button>
          </div>

          {reviewsTab === 'details' && (
            <>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Status</span>
                <span className={styles.detailVal}>
                  <span className={`${styles.statusBadge} ${styles[statusClass]}`}>
                    <span className={styles.statusDot} />
                    {statusClass}
                  </span>
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Total Reviews</span>
                <span className={styles.detailVal}>
                  {r.response?.totalReviews ?? <em className={styles.muted}>Pending</em>}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Unanswered Reviews</span>
                <span className={styles.detailVal}>
                  {r.response?.unansweredReviews ?? <em className={styles.muted}>Pending</em>}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Average Rating</span>
                <span className={styles.detailVal}>
                  {r.response?.averageRating != null ? `${r.response.averageRating} ★` : <em className={styles.muted}>Pending</em>}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Est. Lost Revenue</span>
                <span className={styles.detailVal}>
                  {r.response?.estimatedLostRevenue != null
                    ? `$${r.response.estimatedLostRevenue.toLocaleString()}`
                    : <em className={styles.muted}>Pending</em>}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Summary</span>
                <span className={styles.detailVal} style={{ textAlign: 'left', maxWidth: '60ch' }}>
                  {r.response?.summary || <em className={styles.muted}>No summary yet</em>}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Processed At</span>
                <span className={styles.detailVal}>{formatDate(r.response?.processedAt)}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Google Maps URL</span>
                <span className={styles.detailVal}>
                  {r.googleMapsUrl || <em className={styles.muted}>Not provided</em>}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Place ID</span>
                <span className={styles.detailVal}>
                  {r.placeId || <em className={styles.muted}>Missing</em>}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailKey}>Actions</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    className={styles.addPlaceBtn}
                    onClick={() => {
                      setShowPlaceModal(true);
                      setPlaceMode('url');
                      setPlaceEntry(r.googleMapsUrl || '');
                      setPlaceError(null);
                    }}
                  >
                    <Icon d="M12 5v14M5 12h14" size={14} />
                    {r.googleMapsUrl ? 'Edit Google Maps URL' : 'Add Google Maps URL'}
                  </button>

                  <button
                    type="button"
                    className={styles.addPlaceBtn}
                    onClick={() => {
                      setShowPlaceModal(true);
                      setPlaceMode('placeId');
                      setPlaceEntry(r.placeId || '');
                      setPlaceError(null);
                    }}
                  >
                    <Icon d="M12 5v14M5 12h14" size={14} />
                    {r.placeId ? 'Edit Place ID' : 'Add Place ID'}
                  </button>
                </div>
              </div>
            </>
          )}

          {reviewsTab === 'reviews' && (
            <>
              {r.response?.reviews && r.response.reviews.length > 0 ? (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>Rating Summary</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px' }}>
                      {[5, 4, 3, 2, 1].map((star) => {
                        const count = r.response.reviews.filter((rev) => {
                          const rating = parseReviewRating(rev);
                          return rating != null && Math.round(rating) === star;
                        }).length;
                        return (
                          <div key={star} style={{ padding: '8px 12px', background: '#f3f4f6', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>{star} ★</div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#3b82f6' }}>{count}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Author</th>
                          <th>Rating</th>
                          <th>Review Text</th>
                          <th>Posted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.response.reviews.map((review, idx) => (
                          <tr key={idx}>
                            <td className={styles.businessCell}>{review.author || '—'}</td>
                            <td style={{ textAlign: 'center', fontWeight: '500' }}>{review.rating} ★</td>
                            <td style={{ maxWidth: '400px', fontSize: '0.875rem', textAlign: 'left' }}>{review.review || '—'}</td>
                            <td className={styles.locationCell}>{formatDate(review.postedAt) || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className={styles.emptyState}>
                  <div className={styles.emptyStateIcon}>
                    <Icon d={['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z']} size={40} />
                  </div>
                  <div className={styles.emptyStateText}>No reviews available yet.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showPlaceModal && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
          <div className={styles.modalPanel}>
            <div className={styles.modalHeader}>Add Map URL / Place ID</div>
            <div className={styles.fieldGroup} style={{ marginBottom: '16px' }}>
              <label className={styles.fieldLabel} htmlFor="placeEntry">
                {placeMode === 'placeId' ? 'Place ID' : 'Google Maps URL'}
              </label>
              <textarea
                id="placeEntry"
                className={styles.fieldInput}
                rows={4}
                value={placeEntry}
                onChange={(e) => setPlaceEntry(e.target.value)}
                placeholder={placeMode === 'placeId' ? 'Paste the Google Maps Place ID here' : 'Paste a Google Maps URL here'}
              />
            </div>
            {placeError && (
              <div className={styles.alertDanger} role="alert" style={{ marginBottom: '16px' }}>
                <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                {placeError}
              </div>
            )}
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.removePointBtn}
                onClick={() => {
                  setShowPlaceModal(false);
                  setPlaceMode('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleSavePlaceInfo}
                disabled={savingPlaceInfo}
              >
                {savingPlaceInfo ? <SpinnerIcon size={16} /> : <Icon d="M5 3l14 9-14 9V3z" size={16} />} 
                {savingPlaceInfo ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ConfirmDialog = ({ isOpen, title, message, confirmText, loading, error, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div className={styles.modalPanel}>
        <div className={styles.modalHeader}>{title}</div>
        <div className={styles.fieldGroup} style={{ marginBottom: '16px' }}>
          <p className={styles.confirmMessage}>{message}</p>
        </div>
        {error && (
          <div className={styles.alertDanger} role="alert" style={{ marginBottom: '16px' }}>
            <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            {error}
          </div>
        )}
        <div className={styles.modalActions}>
          <button type="button" className={styles.removePointBtn} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={styles.saveBtn} onClick={onConfirm} disabled={loading}>
            {loading ? <SpinnerIcon size={16} /> : <Icon d="M5 3l14 9-14 9V3z" size={16} />}
            {loading ? 'Deleting…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PANEL 2 — Audit Requests Table
// ═══════════════════════════════════════════════════════════════════════════════
const AuditRequests = ({ token, onViewDetail }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const data = await safeApiFetch(getApiUrl('/api/audit/admin/all'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRequests(data.auditRequests || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const openDeleteConfirm = (request) => {
    setDeleteTarget(request);
    setDeleteError(null);
  };

  const closeDeleteConfirm = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleDeleteRequest = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await safeApiFetch(getApiUrl(`/api/audit/admin/${deleteTarget._id}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setRequests((prev) => prev.filter((r) => r._id !== deleteTarget._id));
      closeDeleteConfirm();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete audit request.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <SpinnerIcon size={28} />
        <span>Loading audit requests…</span>
      </div>
    );
  }

  return (
    <>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>Audit Requests</div>
        <div className={styles.sectionDesc}>
          All audit submissions from users. Click <strong>Details</strong> to view the full request and response data.
        </div>
      </div>

      {error && (
        <div className={styles.alertDanger} role="alert">
          <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          {error}
        </div>
      )}

      <div className={styles.tableWrap}>
        <div className={styles.tableTopbar}>
          <span className={styles.tableTitle}>All Requests</span>
          <span className={styles.tableCount}>{requests.length} total</span>
        </div>

        {requests.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <Icon d={['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z']} size={40} />
            </div>
            <div className={styles.emptyStateText}>No audit requests yet.</div>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Business</th>
                <th>Location</th>
                <th>Submitted By</th>
                <th>Status</th>
                <th>Submitted At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => {
                const statusKey = r.response?.status || 'pending';
                return (
                  <tr key={r._id}>
                    <td className={styles.businessCell}>
                      {r.business}
                      {!r.placeId && (
                        <span className={styles.missingPlaceId} title="Missing Place ID">
                          <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" size={14} />
                        </span>
                      )}
                    </td>
                    <td className={styles.locationCell}>{r.city}, {r.state}</td>
                    <td className={styles.locationCell}>
                      {r.userId?.name
                        ? <>{r.userId.name}<br /><small>{r.userId.email}</small></>
                        : <em>Anonymous</em>}
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[statusKey]}`}>
                        <span className={styles.statusDot} />
                        {statusKey}
                      </span>
                    </td>
                    <td className={styles.locationCell}>{formatDate(r.createdAt)}</td>
                    <td className={styles.rowActions}>
                      <button
                        type="button"
                        className={styles.detailBtn}
                        onClick={() => onViewDetail(r._id)}
                      >
                        <Icon d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0" size={14} />
                        Details
                      </button>
                      <button
                        type="button"
                        className={styles.deleteBtn}
                        onClick={() => openDeleteConfirm(r)}
                        title="Delete audit request"
                      >
                        <Icon d="M3 6h18M9 6V4h6v2M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <ConfirmDialog
          isOpen={!!deleteTarget}
          title="Delete audit request"
          message={`Are you sure you want to delete the audit request for "${deleteTarget?.business || 'this business'}"? This action cannot be undone.`}
          confirmText="Delete"
          loading={deleting}
          error={deleteError}
          onConfirm={handleDeleteRequest}
          onCancel={closeDeleteConfirm}
        />
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Business Directory Selection Modal
// ═══════════════════════════════════════════════════════════════════════════════
const BusinessDirectoryModal = ({ isOpen, onClose, token, onSelect, selectedId, initialSelection }) => {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [minRating, setMinRating] = useState('');
  const [minReviews, setMinReviews] = useState('');
  const [months, setMonths] = useState('3');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Multi-selection states
  const [selectedBizMap, setSelectedBizMap] = useState({});
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [excludedMap, setExcludedMap] = useState({});

  const limit = 25;

  // Load distinct categories once when modal is opened
  useEffect(() => {
    if (!isOpen || !token) return;
    const loadCategories = async () => {
      try {
        const data = await safeApiFetch(getApiUrl('/api/scraper/business-categories'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCategories(data.categories || []);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, [isOpen, token]);

  const fetchBusinesses = useCallback(async (currentPage, query, cat, rating, reviews) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
      });
      if (query && query.trim()) params.append('search', query.trim());
      if (cat && cat !== 'all') params.append('category', cat);
      if (rating) params.append('minRating', rating);
      if (reviews) params.append('minReviews', reviews);

      const url = getApiUrl(`/api/scraper/businesses?${params.toString()}`);
      const data = await safeApiFetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBusinesses(data.businesses || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to load businesses:', err);
    } finally {
      setLoading(false);
    }
  }, [token, limit]);

  // Trigger search whenever search query, category, rating, or reviews change
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      setPage(1);
      fetchBusinesses(1, search, selectedCategory, minRating, minReviews);
    }, 250);
    return () => clearTimeout(timer);
  }, [isOpen, search, selectedCategory, minRating, minReviews, fetchBusinesses]);

  // Page change handler
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage);
      fetchBusinesses(newPage, search, selectedCategory, minRating, minReviews);
    }
  };

  // Helper to check if a business is currently selected
  const isBizSelected = useCallback((bizId) => {
    if (selectAllMatching) {
      return !excludedMap[bizId];
    }
    return Boolean(selectedBizMap[bizId]);
  }, [selectAllMatching, excludedMap, selectedBizMap]);

  // Toggle single business selection (checks or unchecks)
  const toggleBiz = (biz) => {
    const bizId = biz._id;
    if (selectAllMatching) {
      setExcludedMap((prev) => {
        const next = { ...prev };
        if (next[bizId]) {
          delete next[bizId]; // re-include
        } else {
          next[bizId] = true; // exclude/uncheck
        }
        return next;
      });
    } else {
      setSelectedBizMap((prev) => {
        const next = { ...prev };
        if (next[bizId]) {
          delete next[bizId]; // UNCHECK!
        } else {
          next[bizId] = biz; // CHECK!
        }
        return next;
      });
    }
  };

  // Toggle Select All (across all pages matching current filter)
  const handleToggleSelectAll = () => {
    if (selectAllMatching) {
      setSelectAllMatching(false);
      setExcludedMap({});
      setSelectedBizMap({});
    } else {
      setSelectAllMatching(true);
      setExcludedMap({});
      setSelectedBizMap({});
    }
  };

  const totalSelectedCount = selectAllMatching
    ? Math.max(0, totalCount - Object.keys(excludedMap).length)
    : Object.keys(selectedBizMap).length;

  // Header checkbox state (checked if selectAllMatching or all visible items selected)
  const isHeaderChecked = selectAllMatching
    ? Object.keys(excludedMap).length === 0
    : businesses.length > 0 && businesses.every((b) => selectedBizMap[b._id]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (totalSelectedCount > 0) {
      const selectedList = Object.values(selectedBizMap);
      const parsedMonths = months !== '' && !isNaN(parseInt(months, 10)) && parseInt(months, 10) > 0
        ? parseInt(months, 10)
        : null;

      onSelect({
        selectAllMatching,
        selectedBusinesses: selectedList,
        totalSelectedCount,
        maxMonths: parsedMonths,
        filterQuery: {
          search,
          category: selectedCategory,
          minRating,
          minReviews,
        },
      });
      onClose();
    }
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.bizModalPanel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.bizModalHeader}>
          <div>
            <div className={styles.bizModalTitle}>
              Select Business from Directory
              <span className={styles.bizStatsBadge}>
                <Icon d="M3 21h18M3 7v1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7M4 21V11m16 10V11" size={13} />
                {totalCount} locations found
              </span>
            </div>
            <div style={{ fontSize: '0.78rem', color: '#8b949e', marginTop: '3px' }}>
              Filter by category, rating, and review count to select one or multiple locations for review scraping.
            </div>
          </div>
          <button
            type="button"
            className={styles.removePointBtn}
            onClick={onClose}
            style={{ padding: '6px 10px', fontSize: '0.8rem' }}
          >
            ✕
          </button>
        </div>

        {/* Filter Controls Row */}
        <div className={styles.bizModalSearchWrap}>
          <div className={styles.bizFilterRow}>
            <div>
              <input
                type="text"
                className={styles.bizModalSearchInput}
                placeholder="Search by name, area, address..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <select
                className={styles.bizModalSelect}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories ({categories.length})</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                className={styles.bizModalSelect}
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
              >
                <option value="">All Ratings</option>
                <option value="4.5">★ 4.5 & above</option>
                <option value="4.0">★ 4.0 & above</option>
                <option value="3.5">★ 3.5 & above</option>
                <option value="3.0">★ 3.0 & above</option>
                <option value="2.0">★ 2.0 & above</option>
              </select>
            </div>
            <div>
              <select
                className={styles.bizModalSelect}
                value={minReviews}
                onChange={(e) => setMinReviews(e.target.value)}
              >
                <option value="">All Reviews</option>
                <option value="10">10+ reviews</option>
                <option value="25">25+ reviews</option>
                <option value="50">50+ reviews</option>
                <option value="100">100+ reviews</option>
                <option value="250">250+ reviews</option>
                <option value="500">500+ reviews</option>
              </select>
            </div>
            <div>
              <input
                type="number"
                min="1"
                max="120"
                className={styles.bizModalSearchInput}
                placeholder="Months (default: 3)"
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                title="Fetch reviews from last X months (leave blank for all reviews)"
              />
            </div>
          </div>
        </div>

        {/* Select All Banner Notification */}
        {selectAllMatching && (
          <div
            style={{
              padding: '8px 24px',
              background: 'rgba(168, 85, 247, 0.12)',
              borderBottom: '1px solid rgba(168, 85, 247, 0.25)',
              color: '#c084fc',
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span>
              ✓ <strong>All {totalSelectedCount} locations</strong> matching current filter are selected (across all pages).
            </span>
            <button
              type="button"
              onClick={handleToggleSelectAll}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#ffa198',
                cursor: 'pointer',
                fontSize: '0.78rem',
                textDecoration: 'underline',
              }}
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Modal Results Table */}
        <div className={styles.bizModalBody}>
          {loading ? (
            <div className={styles.loadingWrap} style={{ padding: '36px' }}>
              <SpinnerIcon size={24} />
              <span>Loading businesses...</span>
            </div>
          ) : businesses.length === 0 ? (
            <div className={styles.emptyState} style={{ padding: '36px' }}>
              <div className={styles.emptyStateText}>No businesses matched your selected filters.</div>
            </div>
          ) : (
            <table className={styles.bizModalTable}>
              <thead>
                <tr>
                  <th style={{ width: '44px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isHeaderChecked}
                      onChange={handleToggleSelectAll}
                      title="Select All (including beyond current page)"
                      style={{ cursor: 'pointer', accentColor: '#a855f7' }}
                    />
                  </th>
                  <th>Business Name</th>
                  <th>Category</th>
                  <th>Area</th>
                  <th>Total Ratings</th>
                  <th>Place ID</th>
                </tr>
              </thead>
              <tbody>
                {businesses.map((biz) => {
                  const isChecked = isBizSelected(biz._id);
                  return (
                    <tr
                      key={biz._id}
                      className={`${styles.bizModalRow} ${isChecked ? styles.bizModalRowSelected : ''}`}
                      onClick={() => toggleBiz(biz)}
                    >
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleBiz(biz)}
                          style={{ cursor: 'pointer', accentColor: '#a855f7' }}
                        />
                      </td>
                      <td style={{ fontWeight: 600, color: '#e6edf3' }}>
                        {biz.name}
                        {biz.address && (
                          <div style={{ fontSize: '0.72rem', color: '#8b949e', fontWeight: 400, marginTop: '2px' }}>
                            {biz.address}
                          </div>
                        )}
                      </td>
                      <td>
                        {biz.category ? (
                          <span className={`${styles.bizBadge} ${styles.bizBadgeCategory}`}>
                            {biz.category}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        {biz.area ? (
                          <span className={styles.bizBadge}>{biz.area}</span>
                        ) : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {biz.rating ? (
                            <span style={{ color: '#e3b341', fontWeight: 700 }}>★ {biz.rating}</span>
                          ) : null}
                          <span style={{ color: '#8b949e', fontSize: '0.78rem' }}>
                            ({biz.userRatingsTotal != null ? biz.userRatingsTotal : 0} reviews)
                          </span>
                        </div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#8b949e' }}>
                        {biz.placeId}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal Footer with Pagination & Action Buttons */}
        <div className={styles.bizModalFooter}>
          <div className={styles.bizPagination}>
            <button
              type="button"
              className={styles.bizPageBtn}
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1 || loading}
            >
              ← Prev
            </button>
            <span>
              Page <strong style={{ color: '#e6edf3' }}>{page}</strong> of <strong style={{ color: '#e6edf3' }}>{totalPages}</strong> ({totalCount} total)
            </span>
            <button
              type="button"
              className={styles.bizPageBtn}
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages || loading}
            >
              Next →
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '0.8rem', color: totalSelectedCount > 0 ? '#c084fc' : '#8b949e', marginRight: '6px' }}>
              Selected: <strong>{totalSelectedCount} {totalSelectedCount === 1 ? 'business' : 'businesses'}</strong>
              {months && <span> ({months} mos)</span>}
            </span>
            <button
              type="button"
              className={styles.backBtn}
              onClick={onClose}
              style={{ width: 'auto', padding: '7px 14px' }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleConfirm}
              disabled={totalSelectedCount === 0}
              style={{ padding: '7px 16px' }}
            >
              Confirm Selection ({totalSelectedCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PANEL 4 — Scraper Control Center
// ═══════════════════════════════════════════════════════════════════════════════
const ScraperControl = ({ token, onViewDetail }) => {
  const [sourceType, setSourceType] = useState('auditRequest'); // 'auditRequest' | 'business'
  const [requests, setRequests] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null); // single object or multi-selection summary
  const [placeId, setPlaceId] = useState('');
  const [maxMonths, setMaxMonths] = useState(3);
  const [isBizModalOpen, setIsBizModalOpen] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const [jobQueueTab, setJobQueueTab] = useState('inProgress'); // 'inProgress' | 'completed'
  const [resettingJobId, setResettingJobId] = useState(null);
  const [resettingAllCompleted, setResettingAllCompleted] = useState(false);
  const [resettingAllInProgress, setResettingAllInProgress] = useState(false);
  const [enrichingJobId, setEnrichingJobId] = useState(null);
  const [enrichingAllCompleted, setEnrichingAllCompleted] = useState(false);
  const [isQueuePaused, setIsQueuePaused] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);
  const [completedSortField, setCompletedSortField] = useState('diff'); // 'diff' | 'id'
  const [completedSortOrder, setCompletedSortOrder] = useState('desc'); // 'desc' | 'asc'

  const activeRequest = useMemo(
    () => requests.find((r) => r._id === selectedRequestId),
    [requests, selectedRequestId]
  );

  const sortJobsAscending = useCallback((list) => {
    return [...list].sort((a, b) => {
      // 1. 'active' jobs always at the very top
      if (a.state === 'active' && b.state !== 'active') return -1;
      if (b.state === 'active' && a.state !== 'active') return 1;

      // 2. Numerical Job ID ascending (FIFO queue order)
      const numA = parseInt(a.id, 10);
      const numB = parseInt(b.id, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return (a.timestamp || 0) - (b.timestamp || 0);
    });
  }, []);

  const inProgressJobs = useMemo(
    () => sortJobsAscending(jobs.filter((job) => job.state !== 'completed')),
    [jobs, sortJobsAscending]
  );

  const completedJobs = useMemo(() => {
    const list = jobs.filter((job) => job.state === 'completed');

    if (completedSortField === 'diff') {
      return [...list].sort((a, b) => {
        const diffA = Math.max((a.reviewCount || 0) - (a.respondedCount || 0), 0);
        const diffB = Math.max((b.reviewCount || 0) - (b.respondedCount || 0), 0);

        if (diffA !== diffB) {
          return completedSortOrder === 'desc' ? diffB - diffA : diffA - diffB;
        }
        // Secondary sort: total reviews descending
        if ((a.reviewCount || 0) !== (b.reviewCount || 0)) {
          return (b.reviewCount || 0) - (a.reviewCount || 0);
        }
        // Tertiary fallback: Job ID
        return parseInt(b.id, 10) - parseInt(a.id, 10);
      });
    }

    return sortJobsAscending(list);
  }, [jobs, completedSortField, completedSortOrder, sortJobsAscending]);

  const displayedJobs = jobQueueTab === 'completed' ? completedJobs : inProgressJobs;

  const toggleCompletedDiffSort = () => {
    if (completedSortField === 'diff') {
      setCompletedSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setCompletedSortField('diff');
      setCompletedSortOrder('desc');
    }
  };

  useEffect(() => {
    if (sourceType === 'auditRequest') {
      if (!selectedRequestId) {
        setPlaceId('');
        return;
      }
      setPlaceId(activeRequest?.placeId || '');
    } else if (sourceType === 'business') {
      if (!selectedBusiness) {
        setPlaceId('');
        return;
      }
      if (selectedBusiness.isBulk || selectedBusiness.totalSelectedCount > 1 || selectedBusiness.selectAllMatching) {
        setPlaceId(`Multiple (${selectedBusiness.totalSelectedCount} locations — auto-resolved)`);
      } else if (selectedBusiness.placeId) {
        setPlaceId(selectedBusiness.placeId);
      } else if (selectedBusiness.selectedBusinesses?.[0]?.placeId) {
        setPlaceId(selectedBusiness.selectedBusinesses[0].placeId);
      }
    }
  }, [sourceType, activeRequest, selectedRequestId, selectedBusiness]);

  const fetchRequests = useCallback(async () => {
    try {
      const data = await safeApiFetch(getApiUrl('/api/audit/admin/all'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(data.auditRequests || []);
    } catch (err) {
      console.error('Failed to load audit requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  }, [token]);

  const fetchJobs = useCallback(async () => {
    try {
      const data = await safeApiFetch(getApiUrl('/api/scraper/jobs'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobs(data.jobs || []);
      if (typeof data.isPaused === 'boolean') {
        setIsQueuePaused(data.isPaused);
      }
    } catch (err) {
      console.error('Failed to load scraper jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchRequests();
    fetchJobs();

    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [token, fetchRequests, fetchJobs]);

  const handleTogglePause = async () => {
    try {
      setTogglingPause(true);
      setError(null);
      const action = isQueuePaused ? 'resume' : 'pause';
      const res = await safeApiFetch(getApiUrl(`/api/scraper/${action}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsQueuePaused(Boolean(res.isPaused));
      setStatus(res.message);
      await fetchJobs();
    } catch (err) {
      setError(err.message || `Failed to ${isQueuePaused ? 'resume' : 'pause'} scraper.`);
    } finally {
      setTogglingPause(false);
    }
  };

  const handleSourceChange = (newSource) => {
    setSourceType(newSource);
    setError(null);
    setStatus(null);
    if (newSource === 'auditRequest') {
      setPlaceId(activeRequest?.placeId || '');
    } else {
      if (selectedBusiness?.isBulk || selectedBusiness?.totalSelectedCount > 1) {
        setPlaceId(`Multiple (${selectedBusiness.totalSelectedCount} locations — auto-resolved)`);
      } else {
        setPlaceId(selectedBusiness?.placeId || selectedBusiness?.selectedBusinesses?.[0]?.placeId || '');
      }
    }
  };

  const handleRunScrape = async (e) => {
    e.preventDefault();

    if (sourceType === 'business') {
      if (!selectedBusiness) {
        setError('Please select at least one business from the directory.');
        return;
      }
    } else {
      if (!selectedRequestId || !placeId) {
        setError('Please select an audit request and enter a Google Maps Place ID.');
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    setStatus(null);

    const parsedMonths = maxMonths !== '' && !isNaN(parseInt(maxMonths, 10)) && parseInt(maxMonths, 10) > 0
      ? parseInt(maxMonths, 10)
      : null;

    try {
      let payload = {
        maxMonths: parsedMonths,
      };

      if (sourceType === 'business') {
        const isBulk = selectedBusiness.selectAllMatching || selectedBusiness.totalSelectedCount > 1;
        if (isBulk) {
          payload = {
            ...payload,
            sourceType: 'business',
            selectAllMatching: Boolean(selectedBusiness.selectAllMatching),
            filterQuery: selectedBusiness.filterQuery,
            targetIds: selectedBusiness.selectedBusinesses?.map((b) => b._id),
          };
        } else {
          const singleBiz = selectedBusiness.selectedBusinesses?.[0] || selectedBusiness;
          payload = {
            ...payload,
            sourceType: 'business',
            targetId: singleBiz._id,
            placeId: placeId.trim() || singleBiz.placeId,
          };
        }
      } else {
        payload = {
          ...payload,
          sourceType: 'auditRequest',
          targetId: selectedRequestId,
          placeId: placeId.trim(),
        };
      }

      const res = await safeApiFetch(getApiUrl('/api/scraper/run'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      setStatus(res.message || 'Scraper job successfully enqueued!');
      if (sourceType === 'auditRequest') {
        setSelectedRequestId('');
      }
      fetchJobs();
    } catch (err) {
      setError(err.message || 'Failed to trigger scraper.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetJob = async (jobId) => {
    if (!window.confirm('Reset this scraper job? Review counters will be reset to 0 and the job will be re-queued to scrape again.')) return;
    try {
      setResettingJobId(jobId);
      setError(null);
      const res = await safeApiFetch(getApiUrl(`/api/scraper/jobs/${jobId}/reset`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.message || 'Job reset successfully and re-queued!');
      setJobQueueTab('inProgress');
      await fetchJobs();
      if (sourceType === 'auditRequest' && fetchRequests) {
        fetchRequests();
      }
    } catch (err) {
      setError(err.message || 'Failed to reset job.');
    } finally {
      setResettingJobId(null);
    }
  };

  const handleResetAllCompleted = async () => {
    if (!completedJobs.length) return;
    if (!window.confirm(`Reset all ${completedJobs.length} completed location(s)? Review & response counters will be reset to 0 and all locations will be re-queued to scrape again.`)) return;
    try {
      setResettingAllCompleted(true);
      setError(null);
      const res = await safeApiFetch(getApiUrl('/api/scraper/jobs/reset-all-completed'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.message || 'All completed jobs reset and re-queued!');
      setJobQueueTab('inProgress');
      await fetchJobs();
      if (sourceType === 'auditRequest' && fetchRequests) {
        fetchRequests();
      }
    } catch (err) {
      setError(err.message || 'Failed to reset all completed jobs.');
    } finally {
      setResettingAllCompleted(false);
    }
  };

  const handleResetAllInProgress = async () => {
    if (!inProgressJobs.length) return;
    if (!window.confirm(`Reset all ${inProgressJobs.length} in-progress / failed location(s)? Review & response counters will be reset to 0 and all locations will be re-queued to scrape again.`)) return;
    try {
      setResettingAllInProgress(true);
      setError(null);
      const res = await safeApiFetch(getApiUrl('/api/scraper/jobs/reset-all-inprogress'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.message || 'All in-progress jobs reset and re-queued!');
      await fetchJobs();
      if (sourceType === 'auditRequest' && fetchRequests) {
        fetchRequests();
      }
    } catch (err) {
      setError(err.message || 'Failed to reset all in-progress jobs.');
    } finally {
      setResettingAllInProgress(false);
    }
  };

  const handleEnrichEmail = async (job) => {
    const targetId = job.targetId || job.data?.targetId || job.data?.auditRequestId;
    if (!targetId) {
      setError('No target ID found for this location.');
      return;
    }
    try {
      setEnrichingJobId(job.id);
      setError(null);
      const res = await safeApiFetch(getApiUrl('/api/email-enrichment/run'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ targetId }),
      });
      setStatus(res.message || 'Email enrichment job queued!');
      await fetchJobs();
    } catch (err) {
      setError(err.message || 'Failed to queue email enrichment.');
    } finally {
      setEnrichingJobId(null);
    }
  };

  const handleEnrichAllCompleted = async () => {
    // Filter completed jobs that have a valid targetId and no email yet
    const eligibleJobs = completedJobs.filter((j) => {
      const targetId = j.targetId || j.data?.targetId || j.data?.auditRequestId;
      return targetId && !j.email;
    });

    if (!eligibleJobs.length) {
      alert('All completed locations already have emails found or have no valid target ID.');
      return;
    }

    if (!window.confirm(`Queue email enrichment for ${eligibleJobs.length} completed location(s)?`)) return;

    try {
      setEnrichingAllCompleted(true);
      setError(null);
      const targetIds = eligibleJobs.map((j) => j.targetId || j.data?.targetId || j.data?.auditRequestId);
      const res = await safeApiFetch(getApiUrl('/api/email-enrichment/run'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ targetIds }),
      });
      setStatus(res.message || `Queued ${targetIds.length} email enrichment job(s)!`);
      await fetchJobs();
    } catch (err) {
      setError(err.message || 'Failed to queue bulk email enrichment.');
    } finally {
      setEnrichingAllCompleted(false);
    }
  };

  const handleRemoveJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to remove this job from history?')) return;
    try {
      await safeApiFetch(getApiUrl(`/api/scraper/jobs/${jobId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobs((prev) => prev.filter((job) => job.id !== jobId));
    } catch (err) {
      setError(err.message || 'Failed to remove job.');
    }
  };

  const getDuration = (job) => {
    if (!job.processedOn) return '—';
    const start = job.processedOn;
    const end = job.finishedOn || Date.now();
    const diff = end - start;
    if (diff < 0) return '—';
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const singleSelectedBiz = selectedBusiness?.selectedBusinesses?.length === 1 && !selectedBusiness.selectAllMatching
    ? selectedBusiness.selectedBusinesses[0]
    : selectedBusiness?.name ? selectedBusiness : null;

  const isBulkSelection = selectedBusiness?.selectAllMatching || (selectedBusiness?.totalSelectedCount > 1);

  return (
    <>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>Scraper Control Center</div>
        <div className={styles.sectionDesc}>
          Manually trigger and monitor Google Maps review scraper jobs from Audit Requests or the Businesses Directory.
        </div>
      </div>

      {error && (
        <div className={styles.alertDanger} role="alert">
          <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          {error}
        </div>
      )}
      {status && (
        <div className={styles.alertSuccess} role="alert">
          <Icon d={['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4 12 14.01l-3-3']} />
          {status}
        </div>
      )}

      {/* Trigger Scrape Card */}
      <div className={styles.packageCard}>
        <div className={styles.packageCardHeader}>
          <span className={styles.packageName}>Trigger New Scrape</span>
          <span className={styles.packageBadge}>Manual Process</span>
        </div>

        <div style={{ padding: '16px 20px 0' }}>
          {/* Source Switcher */}
          <div className={styles.sourceTabs}>
            <button
              type="button"
              className={`${styles.sourceTabBtn} ${sourceType === 'auditRequest' ? styles.sourceTabBtnActive : ''}`}
              onClick={() => handleSourceChange('auditRequest')}
            >
              <Icon d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.5L18 8.5V19a2 2 0 0 1-2 2z" size={15} />
              Audit Requests
            </button>
            <button
              type="button"
              className={`${styles.sourceTabBtn} ${sourceType === 'business' ? styles.sourceTabBtnActive : ''}`}
              onClick={() => handleSourceChange('business')}
            >
              <Icon d="M3 21h18M3 7v1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7M4 21V11m16 10V11" size={15} />
              Businesses Directory
            </button>
          </div>
        </div>

        <form onSubmit={handleRunScrape} className={styles.packageCardBody} style={{ paddingTop: '8px' }}>
          <div className={styles.fieldGrid}>
            <div className={styles.fieldGroup}>
              {sourceType === 'auditRequest' ? (
                <>
                  <label className={styles.fieldLabel}>Select Audit Request</label>
                  {loadingRequests ? (
                    <div className={styles.fieldInput} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <SpinnerIcon size={14} /> Loading requests...
                    </div>
                  ) : (
                    <>
                      <select
                        className={styles.fieldInput}
                        value={selectedRequestId}
                        onChange={(e) => setSelectedRequestId(e.target.value)}
                        required
                      >
                        <option value="">-- Choose an audit request --</option>
                        {requests.map((r) => (
                          <option key={r._id} value={r._id}>
                            {r.business} ({r.city}, {r.state}) - {r.response?.status || 'pending'}
                          </option>
                        ))}
                      </select>
                      {activeRequest?.placeId && (
                        <span className={styles.fieldHint}>
                          Existing Place ID: <span className={styles.codeInline}>{activeRequest.placeId}</span>
                        </span>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <label className={styles.fieldLabel}>Select Business(es) from Directory</label>
                  <button
                    type="button"
                    className={styles.bizPickerTrigger}
                    onClick={() => setIsBizModalOpen(true)}
                  >
                    {isBulkSelection ? (
                      <div className={styles.bizSelectedInfo}>
                        <span className={styles.bizSelectedName}>
                          ⚡ {selectedBusiness.totalSelectedCount} businesses selected
                          {selectedBusiness.selectAllMatching && ' (All matching current filter)'}
                        </span>
                        <div className={styles.bizSelectedMeta}>
                          {selectedBusiness.filterQuery?.category && selectedBusiness.filterQuery.category !== 'all' && (
                            <span>🏷️ {selectedBusiness.filterQuery.category}</span>
                          )}
                          {selectedBusiness.filterQuery?.minRating && (
                            <span>⭐ ★ {selectedBusiness.filterQuery.minRating}+</span>
                          )}
                          {selectedBusiness.filterQuery?.minReviews && (
                            <span>💬 {selectedBusiness.filterQuery.minReviews}+ reviews</span>
                          )}
                        </div>
                      </div>
                    ) : singleSelectedBiz ? (
                      <div className={styles.bizSelectedInfo}>
                        <span className={styles.bizSelectedName}>{singleSelectedBiz.name}</span>
                        <div className={styles.bizSelectedMeta}>
                          {singleSelectedBiz.area && <span>📍 {singleSelectedBiz.area}</span>}
                          {singleSelectedBiz.category && <span>🏷️ {singleSelectedBiz.category}</span>}
                          {singleSelectedBiz.userRatingsTotal != null && (
                            <span>⭐ {singleSelectedBiz.rating || 0} ({singleSelectedBiz.userRatingsTotal} ratings)</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#8b949e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={15} />
                        Click to search & select businesses from directory...
                      </span>
                    )}
                    <span className={styles.packageBadge} style={{ background: '#21262d', color: '#c084fc', border: '1px solid #30363d' }}>
                      {selectedBusiness ? 'Change' : 'Browse'}
                    </span>
                  </button>
                  {singleSelectedBiz?.address && (
                    <span className={styles.fieldHint}>{singleSelectedBiz.address}</span>
                  )}
                </>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Google Maps Place ID</label>
              <input
                className={styles.fieldInput}
                type="text"
                value={placeId}
                onChange={(e) => setPlaceId(e.target.value)}
                placeholder="e.g. ChIJN1t_tDeuEmsRUsoyG83VSY4"
                disabled={isBulkSelection}
                required={!isBulkSelection}
              />
              <span className={styles.fieldHint}>
                {isBulkSelection
                  ? 'Place IDs will be auto-resolved from MongoDB for each queued business.'
                  : 'Make sure Chrome is running on port 9222 before starting a job.'}
              </span>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Review Time Window (Months)</label>
              <input
                className={styles.fieldInput}
                type="number"
                min="1"
                max="120"
                value={maxMonths}
                onChange={(e) => setMaxMonths(e.target.value)}
                placeholder="e.g. 3 (leave blank for all reviews)"
              />
              <span className={styles.fieldHint}>
                {maxMonths ? `Scrapes reviews from the last ${maxMonths} months.` : 'Leave blank to scrape ALL available reviews.'}
              </span>
            </div>
          </div>

          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              className={styles.saveBtn}
              disabled={submitting || (sourceType === 'auditRequest' && loadingRequests)}
            >
              {submitting ? <SpinnerIcon size={16} /> : <Icon d="M5 3l14 9-14 9V3z" size={16} />}
              {submitting
                ? 'Enqueuing...'
                : isBulkSelection
                ? `Run Scrape Jobs (${selectedBusiness.totalSelectedCount} businesses)`
                : 'Run Scrape Job'}
            </button>
          </div>
        </form>
      </div>

      {/* Business Directory Modal */}
      <BusinessDirectoryModal
        isOpen={isBizModalOpen}
        onClose={() => setIsBizModalOpen(false)}
        token={token}
        selectedId={singleSelectedBiz?._id}
        onSelect={(selection) => {
          setSelectedBusiness(selection);
          if (selection.maxMonths !== undefined) {
            setMaxMonths(selection.maxMonths ?? '');
          }
        }}
      />

      {/* Jobs History Queue */}
      <div className={styles.tableWrap} style={{ marginTop: '24px' }}>
        <div className={styles.tableTopbar} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span className={styles.tableTitle}>Scraper Job Queue</span>

            {/* In Progress vs Completed tabs */}
            <div className={styles.jobQueueTabs}>
              <button
                type="button"
                className={`${styles.jobQueueTabBtn} ${jobQueueTab === 'inProgress' ? styles.jobQueueTabBtnActive : ''}`}
                onClick={() => setJobQueueTab('inProgress')}
              >
                <Icon d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" size={13} />
                In Progress
                <span className={styles.jobQueueCountBadge}>{inProgressJobs.length}</span>
              </button>

              <button
                type="button"
                className={`${styles.jobQueueTabBtn} ${jobQueueTab === 'completed' ? styles.jobQueueTabBtnActive : ''}`}
                onClick={() => setJobQueueTab('completed')}
              >
                <Icon d={['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4 12 14.01l-3-3']} size={13} />
                Completed
                <span className={styles.jobQueueCountBadge}>{completedJobs.length}</span>
              </button>
            </div>

            {/* Pause / Resume Queue Button */}
            {isQueuePaused ? (
              <button
                type="button"
                className={styles.resumeQueueBtn}
                onClick={handleTogglePause}
                disabled={togglingPause}
                title="Resume scraping remaining jobs in queue"
              >
                {togglingPause ? (
                  <SpinnerIcon size={13} />
                ) : (
                  <Icon d="M5 3l14 9-14 9V3z" size={13} />
                )}
                Resume Queue
              </button>
            ) : (
              <button
                type="button"
                className={styles.pauseQueueBtn}
                onClick={handleTogglePause}
                disabled={togglingPause}
                title="Pause scraper: current location will complete review processing, then subsequent jobs will wait"
              >
                {togglingPause ? (
                  <SpinnerIcon size={13} />
                ) : (
                  <Icon d={['M6 4h4v16H6z', 'M14 4h4v16h-4z']} size={13} />
                )}
                Pause Scraper
              </button>
            )}

            {/* Reset All In-Progress Button (visible in In-Progress tab when in-progress jobs exist) */}
            {jobQueueTab === 'inProgress' && inProgressJobs.length > 0 && (
              <button
                type="button"
                className={styles.resetAllBtn}
                onClick={handleResetAllInProgress}
                disabled={resettingAllInProgress}
                title="Reset review counters to 0 for all in-progress / failed locations and re-queue them to scrape again"
              >
                {resettingAllInProgress ? (
                  <SpinnerIcon size={13} />
                ) : (
                  <Icon d="M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" size={13} />
                )}
                Reset All Locations ({inProgressJobs.length})
              </button>
            )}

            {/* Reset All Completed Button (visible in Completed tab when completed jobs exist) */}
            {jobQueueTab === 'completed' && completedJobs.length > 0 && (
              <>
                <button
                  type="button"
                  className={styles.enrichAllBtn}
                  onClick={handleEnrichAllCompleted}
                  disabled={enrichingAllCompleted}
                  title="Queue Google Places email enrichment for all completed locations without an email"
                >
                  {enrichingAllCompleted ? (
                    <SpinnerIcon size={13} />
                  ) : (
                    <Icon d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" size={13} />
                  )}
                  Enrich Emails ({completedJobs.filter((j) => !j.email).length})
                </button>

                <button
                  type="button"
                  className={styles.resetAllBtn}
                  onClick={handleResetAllCompleted}
                  disabled={resettingAllCompleted}
                  title="Reset review counters to 0 for all completed locations and re-queue them to scrape again"
                >
                  {resettingAllCompleted ? (
                    <SpinnerIcon size={13} />
                  ) : (
                    <Icon d="M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" size={13} />
                  )}
                  Reset All Completed ({completedJobs.length})
                </button>
              </>
            )}
          </div>

          <span className={styles.tableCount}>
            {jobQueueTab === 'completed' ? `${completedJobs.length} completed` : `${inProgressJobs.length} in progress`} ({jobs.length} total)
          </span>
        </div>

        {isQueuePaused && (
          <div className={styles.pausedQueueBanner}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon d={['M6 4h4v16H6z', 'M14 4h4v16h-4z']} size={15} />
              <span>
                <strong>Scraper is Paused:</strong> If a job is currently scraping, it will complete that location cleanly. Subsequent queued jobs will wait until you click <strong>Resume Queue</strong>.
              </span>
            </div>
            <button
              type="button"
              className={styles.resumeQueueBtn}
              onClick={handleTogglePause}
              disabled={togglingPause}
              style={{ padding: '4px 10px', fontSize: '0.75rem' }}
            >
              Resume Now
            </button>
          </div>
        )}

        {loadingJobs && jobs.length === 0 ? (
          <div className={styles.loadingWrap} style={{ padding: '24px' }}>
            <SpinnerIcon size={24} />
            <span>Loading jobs queue…</span>
          </div>
        ) : displayedJobs.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <Icon d={['M12 2v20', 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6']} size={40} />
            </div>
            <div className={styles.emptyStateText}>
              {jobQueueTab === 'completed'
                ? 'No completed scraper jobs yet.'
                : 'No active or queued scraper jobs in progress.'}
            </div>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Source</th>
                <th>Business Name</th>
                <th>
                  {jobQueueTab === 'completed' ? (
                    <button
                      type="button"
                      className={styles.sortHeaderBtn}
                      onClick={toggleCompletedDiffSort}
                      title="Sort by highest unreplied reviews (Reviews - Replied)"
                    >
                      <span>Reviews / Responses</span>
                      <span className={styles.sortIndicatorActive}>
                        {completedSortField === 'diff' ? (completedSortOrder === 'desc' ? ' ▼' : ' ▲') : ' ⇅'}
                      </span>
                    </button>
                  ) : (
                    'Reviews / Responses'
                  )}
                </th>
                <th>Email</th>
                <th>Place ID</th>
                <th>Status</th>
                <th>Queued At</th>
                <th>Duration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedJobs.map((job) => {
                const stateKey = job.state || 'waiting';
                let badgeClass = styles.pending;
                if (stateKey === 'active') badgeClass = styles.processing;
                else if (stateKey === 'completed') badgeClass = styles.completed;
                else if (stateKey === 'failed') badgeClass = styles.failed;

                const isBusinessSource = job.sourceType === 'business';
                const isAuditSource = !isBusinessSource;
                const auditTargetId = isAuditSource ? (job.targetId || job.data?.auditRequestId) : null;
                const isEnrichingThis = enrichingJobId === job.id || job.emailEnrichmentStatus === 'processing';
                const unansweredCount = Math.max((job.reviewCount || 0) - (job.respondedCount || 0), 0);

                return (
                  <tr key={job.id}>
                    <td className={styles.businessCell}>#{job.id}</td>
                    <td>
                      <span
                        className={`${styles.sourceBadge} ${
                          isBusinessSource ? styles.sourceBadgeBusiness : styles.sourceBadgeAudit
                        }`}
                      >
                        {isBusinessSource ? 'Business' : 'Audit'}
                      </span>
                    </td>
                    <td className={styles.locationCell}>{job.businessName}</td>
                    <td>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span className={`${styles.reviewStatPill} ${(job.reviewCount || 0) > 0 ? styles.reviewStatPillHighlight : ''}`}>
                          💬 {job.reviewCount || 0} reviews
                          <span style={{ opacity: 0.65, fontSize: '0.72rem' }}>({job.respondedCount || 0} replied)</span>
                        </span>
                        {jobQueueTab === 'completed' && (
                          <span
                            className={unansweredCount > 0 ? styles.unansweredBadge : styles.unansweredZeroBadge}
                            title={`${unansweredCount} unreplied review(s)`}
                          >
                            {unansweredCount > 0 ? `${unansweredCount} unreplied` : 'All replied'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={styles.locationCell}>
                      {job.email ? (
                        <span className={styles.emailPill} title={`Email: ${job.email}${job.website ? ` (from ${job.website})` : ''}`}>
                          ✉️ <a href={`mailto:${job.email}`} className={styles.emailPillLink}>{job.email}</a>
                        </span>
                      ) : isEnrichingThis ? (
                        <span className={styles.statusBadge} style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#c084fc' }}>
                          <SpinnerIcon size={11} /> Finding…
                        </span>
                      ) : job.emailEnrichmentStatus === 'not_found' ? (
                        <span className={styles.emailNotFoundPill} title="No email could be found on website or Google Places">
                          Not found
                        </span>
                      ) : job.emailEnrichmentStatus === 'failed' ? (
                        <span className={styles.emailNotFoundPill} style={{ color: '#f87171' }} title="Enrichment failed">
                          Failed
                        </span>
                      ) : (
                        <span style={{ color: '#6e7681', fontSize: '0.75rem' }}>—</span>
                      )}
                    </td>
                    <td className={styles.locationCell} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {job.data?.placeId || '—'}
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${badgeClass}`}>
                        <span className={styles.statusDot} />
                        {stateKey}
                      </span>
                    </td>
                    <td className={styles.locationCell}>{formatDate(job.timestamp)}</td>
                    <td className={styles.locationCell}>{getDuration(job)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isAuditSource && (
                          <button
                            type="button"
                            className={styles.detailBtn}
                            style={{ padding: '6px 10px', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                            onClick={() => onViewDetail && onViewDetail(auditTargetId)}
                            disabled={!auditTargetId}
                            title={auditTargetId ? 'View audit details' : 'No linked audit request'}
                          >
                            <Icon d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0" size={14} />
                            Details
                          </button>
                        )}

                        {jobQueueTab === 'completed' && (
                          <button
                            type="button"
                            className={styles.enrichEmailBtn}
                            onClick={() => handleEnrichEmail(job)}
                            disabled={isEnrichingThis || enrichingAllCompleted}
                            title={job.email ? 'Re-fetch email from website' : 'Fetch email address for this business'}
                          >
                            {isEnrichingThis ? (
                              <SpinnerIcon size={12} />
                            ) : (
                              <Icon d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" size={12} />
                            )}
                            {job.email ? 'Re-fetch' : 'Email'}
                          </button>
                        )}

                        <button
                          type="button"
                          className={styles.resetBtn}
                          onClick={() => handleResetJob(job.id)}
                          disabled={resettingJobId === job.id}
                          title="Reset reviews/responses counter to 0 and re-process job"
                        >
                          {resettingJobId === job.id ? (
                            <SpinnerIcon size={12} />
                          ) : (
                            <Icon d="M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" size={13} />
                          )}
                          Reset
                        </button>

                        <button
                          type="button"
                          className={styles.removePointBtn}
                          style={{ padding: '6px 10px', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                          onClick={() => handleRemoveJob(job.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PANEL 5 — Google Connect
// ═══════════════════════════════════════════════════════════════════════════════
const GoogleLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const GoogleConnectPanel = ({ token }) => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchLocations = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const data = await safeApiFetch(getApiUrl('/api/auth/google/locations'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocations(data.locations || []);
    } catch (err) {
      // Gracefully handle missing backend route
      if (err.message.includes('404') || err.message.toLowerCase().includes('non-json')) {
        setLocations([]);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const handleDisconnect = async (locationId) => {
    if (!window.confirm('Are you sure you want to disconnect this Google account?')) return;
    setDisconnecting(locationId);
    setError(null);
    setSuccessMsg(null);
    try {
      await safeApiFetch(getApiUrl(`/api/auth/google/disconnect/${locationId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocations(prev => prev.filter(l => l.locationId !== locationId));
      setSuccessMsg('Google account disconnected successfully.');
    } catch (err) {
      setError(err.message || 'Failed to disconnect account.');
    } finally {
      setDisconnecting(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <SpinnerIcon size={28} />
        <span>Loading connected accounts…</span>
      </div>
    );
  }

  return (
    <>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>Google Business Profile</div>
        <div className={styles.sectionDesc}>
          Connect your Google account to let ReplyShoot fetch reviews and post AI-generated replies on your behalf.
        </div>
      </div>

      {error && (
        <div className={styles.alertDanger} role="alert">
          <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          {error}
        </div>
      )}
      {successMsg && (
        <div className={styles.alertSuccess} role="alert">
          <Icon d={['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4 12 14.01l-3-3']} />
          {successMsg}
        </div>
      )}

      {/* Connected Locations */}
      {locations.length > 0 ? (
        <div className={styles.gcLocationsGrid}>
          {locations.map(loc => (
            <div key={loc.locationId} className={styles.gcLocationCard}>
              <div className={styles.gcLocationHeader}>
                <div className={styles.gcLocationIcon}>
                  <GoogleLogo size={22} />
                </div>
                <div className={styles.gcLocationMeta}>
                  <div className={styles.gcLocationName}>{loc.name || 'Google Business Profile'}</div>
                  <div className={styles.gcLocationId}>{loc.googleAccountId || loc.locationId}</div>
                </div>
                <span className={`${styles.statusBadge} ${styles.completed}`}>
                  <span className={styles.statusDot} />
                  Connected
                </span>
              </div>

              <div className={styles.gcLocationRows}>
                {loc.locationId && (
                  <div className={styles.gcRow}>
                    <span className={styles.gcRowKey}>Location ID</span>
                    <span className={styles.gcRowVal} style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      {loc.locationId}
                    </span>
                  </div>
                )}
                {loc.tokenExpiry && (
                  <div className={styles.gcRow}>
                    <span className={styles.gcRowKey}>Token Expires</span>
                    <span className={styles.gcRowVal}>{formatDate(loc.tokenExpiry)}</span>
                  </div>
                )}
              </div>

              <div className={styles.gcLocationActions}>
                <button
                  type="button"
                  className={styles.gcReconnectBtn}
                  onClick={() => navigate('/connect-google')}
                >
                  <Icon d="M1 4v6h6M23 20v-6h-6" size={13} />
                  Reconnect
                </button>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => handleDisconnect(loc.locationId)}
                  disabled={disconnecting === loc.locationId}
                  title="Disconnect this Google account"
                >
                  {disconnecting === loc.locationId ? (
                    <SpinnerIcon size={13} />
                  ) : (
                    <Icon d="M3 6h18M9 6V4h6v2M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" size={13} />
                  )}
                  {disconnecting === loc.locationId ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.gcEmptyState}>
          <div className={styles.gcEmptyIcon}>
            <GoogleLogo size={32} />
          </div>
          <div className={styles.gcEmptyTitle}>No Google Account Connected</div>
          <div className={styles.gcEmptyDesc}>
            Connect your Google Business Profile to start automating review responses.
          </div>
        </div>
      )}

      {/* Connect button */}
      <div className={styles.gcConnectWrap}>
        <button
          type="button"
          className={styles.gcConnectBtn}
          onClick={() => navigate('/connect-google')}
        >
          <GoogleLogo size={18} />
          {locations.length > 0 ? 'Connect Another Account' : 'Connect Google Account'}
        </button>
        <p className={styles.gcConnectNote}>
          <Icon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" size={13} />
          Secure OAuth 2.0 — we never store your Google password.
        </p>
      </div>
    </>
  );
};

const PANELS = {
  packages: 'packages',
  audits: 'audits',
  auditDetail: 'auditDetail',
  scraper: 'scraper',
  googleConnect: 'googleConnect',
  cronsControl: 'cronsControl',
};

const NAV_ITEMS = [
  {
    id: PANELS.audits,
    label: 'Audit Requests',
    iconPaths: [
      'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z',
      'M14 2v6h6',
      'M16 13H8',
      'M16 17H8',
      'M10 9H8',
    ],
  },
  {
    id: PANELS.packages,
    label: 'Package Management',
    iconPaths: [
      'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z',
      'M3.27 6.96 12 12.01l8.73-5.05',
      'M12 22.08V12',
    ],
  },
  {
    id: PANELS.scraper,
    label: 'Scraper Control',
    iconPaths: [
      'M4 17l6-6-6-6',
      'M12 19h8',
    ],
  },
  {
    id: PANELS.googleConnect,
    label: 'Google Connect',
    iconPaths: [
      'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    ],
  },
  {
    id: PANELS.cronsControl,
    label: 'Crons Control',
    iconPaths: [
      'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z',
      'M12 6v6l4 2',
    ],
  },
];

const PANEL_META = {
  [PANELS.packages]: { title: 'Package Management', subtitle: 'Manage your pricing plans and features' },
  [PANELS.audits]: { title: 'Audit Requests', subtitle: 'Review all submitted audit requests' },
  [PANELS.auditDetail]: { title: 'Audit Detail', subtitle: 'Full audit request information' },
  [PANELS.scraper]: { title: 'Scraper Control', subtitle: 'Manually run and monitor scrape jobs' },
  [PANELS.googleConnect]: { title: 'Google Connect', subtitle: 'Manage connected Google Business Profile accounts' },
  [PANELS.cronsControl]: { title: 'Crons Control', subtitle: 'Manage, monitor, and execute background CRON jobs' },
};

const CronsControl = ({ token }) => {
  const [crons, setCrons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [focusedJobName, setFocusedJobName] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const fetchCrons = useCallback(async () => {
    try {
      const data = await safeApiFetch(getApiUrl('/api/crons'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCrons(data.jobs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCrons();
    const interval = setInterval(fetchCrons, 8000);
    return () => clearInterval(interval);
  }, [fetchCrons]);

  const handleRun = async (name) => {
    setActionLoading(prev => ({ ...prev, [name]: 'run' }));
    try {
      await safeApiFetch(getApiUrl(`/api/crons/${name}/run`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchCrons();
    } catch (err) {
      alert(`Failed to run cron job: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleToggle = async (name) => {
    setActionLoading(prev => ({ ...prev, [name]: 'toggle' }));
    try {
      await safeApiFetch(getApiUrl(`/api/crons/${name}/toggle`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchCrons();
    } catch (err) {
      alert(`Failed to toggle cron job: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleClearLogs = async (name) => {
    if (!window.confirm('Are you sure you want to clear all logs for this cron job?')) return;
    setActionLoading(prev => ({ ...prev, [name]: 'clear' }));
    try {
      await safeApiFetch(getApiUrl(`/api/crons/${name}/logs`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchCrons();
    } catch (err) {
      alert(`Failed to clear logs: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [name]: null }));
    }
  };

  const focusedJob = crons.find(j => j.name === focusedJobName);

  if (loading && crons.length === 0) {
    return (
      <div className={styles.loadingWrap}>
        <SpinnerIcon size={28} />
        <span>Loading CRON jobs…</span>
      </div>
    );
  }

  return (
    <>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>CRON Jobs Manager</div>
        <div className={styles.sectionDesc}>
          Monitor, trigger, and toggle background schedules. Focus on any job to view its live execution log stream.
        </div>
      </div>

      {error && (
        <div className={styles.alertDanger} role="alert">
          <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          {error}
        </div>
      )}

      <div className={styles.cronsLayout}>
        {/* Left Side: Jobs List */}
        <div className={styles.cronsListContainer}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Job Title</th>
                  <th>Schedule</th>
                  <th>Status</th>
                  <th>Last Run</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {crons.map((job) => {
                  const isFocused = job.name === focusedJobName;
                  const isRunning = job.status === 'running';
                  const isRunLoading = actionLoading[job.name] === 'run';
                  const isToggleLoading = actionLoading[job.name] === 'toggle';

                  return (
                    <tr 
                      key={job.name} 
                      className={`${styles.cronRow} ${isFocused ? styles.cronRowFocused : ''}`}
                      onClick={() => setFocusedJobName(job.name)}
                    >
                      <td>
                        <div className={styles.cronTitleCol}>
                          <span className={styles.cronTitleText}>{job.title}</span>
                          <span className={styles.cronDescText}>{job.description}</span>
                        </div>
                      </td>
                      <td>
                        <code className={styles.cronCode}>{job.schedule}</code>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[job.status] || styles.idle}`}>
                          <span className={styles.statusDot} />
                          {job.status}
                        </span>
                      </td>
                      <td className={styles.locationCell}>
                        {job.lastRun ? (
                          <div className={styles.lastRunCol}>
                            <span>{formatDate(job.lastRun)}</span>
                            {job.lastStatus && (
                              <span className={`${styles.lastStatusBadge} ${styles[job.lastStatus]}`}>
                                {job.lastStatus}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className={styles.muted}>Never</span>
                        )}
                      </td>
                      <td>
                        <div className={styles.cronActionsCell} onClick={e => e.stopPropagation()}>
                          <button
                            type="button"
                            className={styles.cronRunBtn}
                            onClick={() => handleRun(job.name)}
                            disabled={isRunning || isRunLoading || !job.isActive}
                            title="Run job immediately"
                          >
                            {isRunning || isRunLoading ? (
                              <SpinnerIcon size={12} />
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polygon points="5 3 19 12 5 21 5 3"/>
                              </svg>
                            )}
                            Run
                          </button>

                          <button
                            type="button"
                            className={`${styles.cronToggleBtn} ${job.isActive ? styles.btnActive : styles.btnInactive}`}
                            onClick={() => handleToggle(job.name)}
                            disabled={isToggleLoading}
                            title={job.isActive ? 'Disable Schedule' : 'Enable Schedule'}
                          >
                            {job.isActive ? 'Active' : 'Paused'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Side: Logs Drawer */}
        <div className={styles.cronsLogsContainer}>
          {focusedJob ? (
            <div className={styles.logsCard}>
              <div className={styles.logsCardHeader}>
                <div>
                  <div className={styles.logsCardTitle}>{focusedJob.title} Logs</div>
                  <div className={styles.logsCardSubtitle}>Showing last 200 execution logs</div>
                </div>
                <div className={styles.logsCardActions}>
                  <button
                    type="button"
                    className={styles.logsRefreshBtn}
                    onClick={fetchCrons}
                    title="Refresh logs"
                  >
                    Refresh
                  </button>
                  <button
                    type="button"
                    className={styles.logsClearBtn}
                    onClick={() => handleClearLogs(focusedJob.name)}
                    disabled={actionLoading[focusedJob.name] === 'clear'}
                    title="Clear log history"
                  >
                    Clear Logs
                  </button>
                </div>
              </div>

              <div className={styles.logsTerminal}>
                {focusedJob.logs && focusedJob.logs.length > 0 ? (
                  focusedJob.logs.map((log, idx) => {
                    const logTypeClass = styles[`log_${log.type}`] || '';
                    return (
                      <div key={idx} className={`${styles.logLine} ${logTypeClass}`}>
                        <span className={styles.logTimestamp}>
                          [{new Date(log.timestamp).toLocaleTimeString()}]
                        </span>
                        <span className={styles.logMessage}>{log.message}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.terminalEmpty}>
                    No logs recorded. Trigger a run or wait for the scheduled time.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.logsPlaceholder}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#8b949e" strokeWidth="1.5">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              <div className={styles.logsPlaceholderText}>Select a CRON Job</div>
              <div className={styles.logsPlaceholderDesc}>
                Click on any cron job row in the list to view its logs, scheduler status, and execution history.
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const DashboardPage = () => {
  const [activePanel, setActivePanel] = useState(PANELS.audits);
  const [selectedAuditId, setSelectedAuditId] = useState(null);
  const navigate = useNavigate();

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user1')) || null; }
    catch { return null; }
  }, []);

  const token = localStorage.getItem('token1');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!token) {
      navigate('/?login=true', { replace: true });
    }
  }, [token, navigate]);

  const goToAuditDetail = useCallback((id) => {
    setSelectedAuditId(id);
    setActivePanel(PANELS.auditDetail);
  }, []);

  const goBackToAudits = useCallback(() => {
    setSelectedAuditId(null);
    setActivePanel(PANELS.audits);
  }, []);

  const meta = PANEL_META[activePanel];

  // ── Render panel content ─────────────────────────────────────────────────
  const renderContent = () => {
    if (!token) {
      return null;
    }
    if (!isAdmin) {
      return (
        <div className={styles.alertWarning} role="alert">
          You do not have admin privileges. Only admins can access this area.
        </div>
      );
    }

    switch (activePanel) {
      case PANELS.packages:
        return <PackageManagement token={token} />;
      case PANELS.audits:
        return <AuditRequests token={token} onViewDetail={goToAuditDetail} />;
      case PANELS.scraper:
        return <ScraperControl token={token} onViewDetail={goToAuditDetail} />;
      case PANELS.googleConnect:
        return <GoogleConnectPanel token={token} />;
      case PANELS.cronsControl:
        return <CronsControl token={token} />;
      case PANELS.auditDetail:
        return (
          <AuditDetail
            token={token}
            requestId={selectedAuditId}
            onBack={goBackToAudits}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.shell}>
      {/* ── Sidebar ───────────────────────────────────────────────────── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Link to="/" className={styles.brand}>
            <span className={styles.brandDot} />
            ReplyShoot
          </Link>
          {user && (
            <div className={styles.userMeta}>
              <div className={styles.userName}>{user.name || user.email}</div>
              {user.role && <div className={styles.userRole}>{user.role}</div>}
            </div>
          )}
        </div>

        <nav className={styles.nav} aria-label="Admin navigation">
          <div className={styles.navLabel}>Admin</div>
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`${styles.navItem} ${activePanel === item.id || (activePanel === PANELS.auditDetail && item.id === PANELS.audits) ? styles.active : ''}`}
              onClick={() => {
                setActivePanel(item.id);
                if (item.id !== PANELS.audits) setSelectedAuditId(null);
              }}
              aria-current={activePanel === item.id ? 'page' : undefined}
            >
              <span className={styles.navIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  strokeLinejoin="round" aria-hidden="true">
                  {item.iconPaths.map((d, i) => <path key={i} d={d} />)}
                </svg>
              </span>
              {item.label}
            </button>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <Link to="/" className={styles.backBtn}>
            <Icon d="M19 12H5M12 5l-7 7 7 7" size={14} />
            Back to site
          </Link>
        </div>
      </aside>

      {/* ── Main ──────────────────────────────────────────────────────── */}
      <div className={styles.main}>
        <header className={styles.topbar} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className={styles.pageTitle}>{meta.title}</div>
            <div className={styles.pageSubtitle}>{meta.subtitle}</div>
          </div>
          <LanguageToggle />
        </header>

        <div className={styles.content}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
